import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { fixedNumberFromInput, getTimeTextForValue } from '../../utils/utils';
import { showAlert } from './alert-service';

interface Props {
	inputType: number;
	value: number | undefined;
	onBlur: (newDisplayValue: number) => void;
	className?: string;
	minValue?: string;
	maxValue?: string;
}

export default function DurationInput(props: Props) {
	const {
		inputType, // can be in seconds, minutes or seconds and minutes
		value, // value from parent component
		onBlur, // function from parent component when unfocusing input field
		className, // optional css class
		minValue, // optional minimum input value
		maxValue, // optional maximum input value
	} = props;

	const [displayValue, setDisplayValue] = useState<string>(
		getTimeTextForValue(value ?? 0, false, inputType),
	);
	const [editValue, setEditValue] = useState<string>(
		getTimeTextForValue(value ?? 0, true, inputType),
	);
	const [editingInput, setEditingInput] = useState<boolean>(false);

	useEffect(() => {
		if (value) {
			setDisplayValue(getTimeTextForValue(value, false, inputType));
			setEditValue(getTimeTextForValue(value, true, inputType));
		}
	}, [value]);

	const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		let updatedValue: string | number = event.target.value;
		updatedValue = updatedValue.replace('s', '');

		const parts = updatedValue.split(':');
		parts.reverse();
		if (parts.length > 1) {
			const hrs = parts[2] || 0;
			const min = parts[1];
			const sec = parts[0];
			updatedValue = ((Number(hrs) * 60 * 60) + (Number(min) * 60) + Number(sec)).toString();
		} else {
			updatedValue = Number(parts[0]).toString();
		}

		let newValue = fixedNumberFromInput(updatedValue);

		// show alert and restore original value if input is not valid
		if (Number.isNaN(Number(updatedValue))) {
			setDisplayValue(getTimeTextForValue(value ?? 0, false, inputType));
			setEditValue(getTimeTextForValue(value ?? 0, true, inputType));
			setEditingInput(false);
			showAlert({
				message: 'Please enter a valid duration',
				duration: 2000,
				type: 'warning',
			});
		} else {
			if (minValue && newValue < Number(minValue)) {
				newValue = Number(minValue);
			}

			if (maxValue && newValue > Number(maxValue)) {
				newValue = Number(maxValue);
			}

			const newDisplayValue = getTimeTextForValue(newValue, false, inputType);
			const newEditValue = getTimeTextForValue(newValue, true, inputType);

			// set component state
			setDisplayValue(newDisplayValue);
			setEditValue(newEditValue);
			setEditingInput(false);

			// set external state
			onBlur(newValue);
		}
	};

	return (
		<div className={classNames(`duration-input ${className ?? ''}`)}>
			<input
				type="text"
				value={editingInput ? editValue : displayValue}
				onFocus={() => setEditingInput(true)}
				onBlur={handleOnBlur}
				onChange={(e) => setEditValue(e.target.value)}
			/>
		</div>
	);
}
